"use client";

import { Fragment } from "react";
import type { ArticleHeroComponent, ArticleHeroContent } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { translateCategoryLabel } from "../../i18n/categories";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";
import { ArticleHeroShare } from "./ArticleHeroShare";

/**
 * Normalize one baked SUBJECT breadcrumb for display. The projector emits the category trail as
 * `?category=` filter links whose labels are the FRENCH category titles; we keep the filter link
 * but translate the label from the static category map (categories are code-first reference data,
 * so their labels translate in code, not per-field).
 *
 * "Articles" is NOT a category — it's the all-articles listing, prepended as a fixed root crumb by
 * the renderer (see {@link ArticleHeroRenderer}). Any legacy `communique-presse` crumb still riding
 * along in older baked data is dropped here (returns null) so it never doubles up with that root.
 */
function normalizeCrumb(
  crumb: { label: string; href?: unknown },
  locale: string,
): { label: string; href: string | undefined } | null {
  const resolved = resolveLinkHref(crumb.href as never) as string | undefined;
  const keyPath = resolved?.match(/[?&]category=([^&#]+)/)?.[1];
  if (keyPath === "communique-presse") {
    return null;
  }
  const label = translateCategoryLabel(locale, keyPath ?? crumb.label);
  return { label, href: resolved };
}

/** Up-to-two initials for the byline fallback avatar: first + last word
 *  initial (e.g. "Nioni Masela" → "NM"), or the first two letters of a
 *  single-word name. Empty string when there is no usable name. */
function getAuthorInitials(name?: string): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Round byline avatar. Renders the author portrait when picture data is
 *  present (`authorImage`); otherwise a brand-blue disc with the author's
 *  initials, so every article shows a consistent, polished byline. */
function AuthorAvatar({ name, image }: { name: string; image?: ArticleHeroContent["authorImage"] }) {
  const resolved = image ? resolveImageValue(image) : { src: "" };
  const src = resolved.src;
  const initials = getAuthorInitials(name);
  return (
    <div
      className="relative grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-b from-[#00A356] to-[#00B862] text-white ring-2 ring-white shadow-[0_2px_8px_-2px_rgba(0,184,98,0.45)]"
      aria-hidden="true"
    >
      {src ? (
        <RuntimeImage
          src={src}
          alt={name}
          width={44}
          height={44}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="text-[15px] font-bold leading-none tracking-wide">{initials}</span>
      )}
    </div>
  );
}

/** Article hero renderer with breadcrumb, image, author and share links. */
export function ArticleHeroRenderer({ component }: { component: ArticleHeroComponent }) {
  const locale = useLocale();
  const {
    eyebrow,
    breadcrumbs = [],
    title,
    author,
    authorRole,
    authorImage,
    publishDate,
    featuredImage,
  } = component.content;
  const { src, options } = resolveImageValue(featuredImage.src);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const fitImageContainerStyle: React.CSSProperties = { minHeight: "min(50vmin, 280px)" };

  // "Articles" is the fixed root crumb (the all-articles listing), always present and always a
  // link — it is not a category. The subject crumbs (Économie, …) follow it, translated. So the
  // hero reads "Articles › Économie" on every article, even those with no subject yet.
  const subjectCrumbs = breadcrumbs
    .map((crumb) => normalizeCrumb(crumb, locale))
    .filter((c): c is { label: string; href: string | undefined } => c !== null);
  const displayCrumbs: { label: string; href: string | undefined }[] = [
    { label: translateCategoryLabel(locale, "communique-presse"), href: "/articles" },
    ...subjectCrumbs,
  ];

  const hasContext = Boolean(eyebrow || displayCrumbs.length > 0);

  return (
    <section className="bg-[#EEF1F0] md:bg-[#E6F4EA] pb-6 pt-6 md:pt-10 lg:pt-10 lg:pb-8 md:rounded-xl px-4 md:px-6 lg:px-10">
      <div className="rounded-3xl" style={{ maxWidth: 900, margin: "0 auto" }}>
        {/* 2 columns from md instead of lg */}
        <div className="grid gap-6 md:gap-8 md:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] md:items-stretch">
          {/* TEXT COLUMN */}
          <div className="space-y-4">
          {hasContext && (
            <div className="flex flex-wrap items-center gap-2 text-sm xl:text-base font-semibold uppercase tracking-wide text-[#00B862]">
              {displayCrumbs.length > 0 && (
                <nav
                  aria-label={t(locale, "breadcrumbAriaLabel")}
                  className="flex flex-wrap items-center gap-2 text-[#6B7280]"
                >
                  {displayCrumbs.map((display, index) => {
                    return (
                      <Fragment key={`${display.label}-${index}`}>
                        {index > 0 && <span className="text-[#A1A1AA]">›</span>}
                        {display.href ? (
                          <RuntimeLink
                            href={display.href}
                            className="hover:text-[#00B862] normal-case font-semibold tracking-normal"
                          >
                            {display.label}
                          </RuntimeLink>
                        ) : (
                          <span className="normal-case font-semibold tracking-normal text-[#121212]">
                            {display.label}
                          </span>
                        )}
                      </Fragment>
                    );
                  })}
                </nav>
              )}
            </div>
          )}

          <h1
            className="text-3xl font-semibold text-[#00B862] md:text-4xl"
            style={{ fontFamily: "var(--font-aleo)" }}
          >
            {title}
          </h1>
          <div className="mt-2 h-1 w-12 rounded-sm bg-[#FFC105]" aria-hidden="true" />

          {/* MOBILE IMAGE: between title and author (more padding from title) */}
          <div className="mt-6 block md:hidden">
            <div
              className={mergeClassNames(
                "relative w-full max-w-[320px] mx-auto overflow-hidden rounded border border-[#E5E7EB]",
                overrideProps.className
              )}
              style={mergeStyles(fitImageContainerStyle, overrideProps.style)}
            >
              <RuntimeImage
                src={src}
                alt={featuredImage.alt}
                className="absolute inset-0 h-full w-full object-cover"
                style={objectPositionStyle}
                priority
              />
            </div>
          </div>

          {/* Summary is intentionally not rendered in the hero — design shows title → author
              → date → share buttons with no description block. The data is preserved in the
              CMS payload so cards/listings/SEO can still consume it. */}

          <div className="flex items-center gap-3 text-sm xl:text-base text-[#52525B]">
            {author ? <AuthorAvatar name={author} image={authorImage} /> : null}
            <div className="min-w-0">
              {author ? (
                <p
                  className="text-[#00B862]"
                  style={{
                    fontFamily: '"Nunito Sans"',
                    fontSize: "18px",
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "127.5%",
                  }}
                >
                  {author}
                  {authorRole ? (
                    <span className="font-normal text-[#6B7280]">
                      , {authorRole}
                    </span>
                  ) : null}
                </p>
              ) : null}
              {publishDate ? (
                <p className="mt-0.5 font-medium text-[#121212]">{formatDisplayDate(publishDate, locale)}</p>
              ) : null}
            </div>
          </div>

            <ArticleHeroShare title={title} />
          </div>

          {/* IMAGE COLUMN ON MD+:
              - Below the title (because title is full-width above the grid)
              - To the right of summary/author/social */}
          <div className="hidden md:flex justify-center md:justify-end md:items-stretch">
            <div
              className={mergeClassNames(
                "relative w-full max-w-[280px] overflow-hidden rounded border border-[#E5E7EB] h-full",
                overrideProps.className
              )}
              style={mergeStyles(fitImageContainerStyle, overrideProps.style)}
            >
              <RuntimeImage
                src={src}
                alt={featuredImage.alt}
                className="absolute inset-0 h-full w-full object-cover"
                style={objectPositionStyle}
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
